import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { Plus, FolderOpen, ExternalLink, Package, RefreshCw, Trash2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function DashboardPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newProject, setNewProject] = useState({ name: '', source_url: '' });
  const [creating, setCreating] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchProjects = async () => {
    try {
      const res = await api.get('/projects');
      setProjects(res.data);
    } catch (err) {
      toast.error('Nepodařilo se načíst projekty');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post('/projects', newProject);
      toast.success('Projekt vytvořen');
      setCreateOpen(false);
      setNewProject({ name: '', source_url: '' });
      fetchProjects();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Chyba při vytváření projektu');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await api.delete(`/projects/${deleteId}`);
      toast.success('Projekt smazán');
      setDeleteId(null);
      fetchProjects();
    } catch (err) {
      toast.error('Chyba při mazání projektu');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8">
        <div className="flex items-center justify-between mb-8">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900">Projekty</h1>
          <p className="text-slate-500 mt-1">Správa XML produktových feedů</p>
        </div>
        
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold" data-testid="create-project-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nový projekt
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Vytvořit nový projekt</DialogTitle>
              <DialogDescription>Zadejte název projektu a URL zdrojového XML feedu</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="project-name">Název projektu</Label>
                  <Input
                    id="project-name"
                    placeholder="Např. Pura Vida Shop"
                    value={newProject.name}
                    onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                    required
                    data-testid="project-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="source-url">URL zdrojového feedu</Label>
                  <Input
                    id="source-url"
                    type="url"
                    placeholder="https://example.com/feed.xml"
                    value={newProject.source_url}
                    onChange={(e) => setNewProject({ ...newProject, source_url: e.target.value })}
                    required
                    data-testid="source-url-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                  Zrušit
                </Button>
                <Button 
                  type="submit" 
                  className="bg-amber-500 hover:bg-amber-600 text-slate-900"
                  disabled={creating}
                  data-testid="create-project-submit"
                >
                  {creating ? 'Vytvářím...' : 'Vytvořit'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {projects.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
            <FolderOpen className="w-8 h-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Žádné projekty</h3>
          <p className="text-slate-500 mb-6 max-w-sm">
            Vytvořte svůj první projekt pro správu XML produktového feedu
          </p>
          <Button 
            onClick={() => setCreateOpen(true)}
            className="bg-amber-500 hover:bg-amber-600 text-slate-900"
            data-testid="empty-create-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Vytvořit projekt
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <Card 
              key={project.id} 
              className="border border-slate-200 hover:border-amber-300 transition-colors"
              data-testid={`project-card-${project.id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-lg font-heading truncate">{project.name}</CardTitle>
                    <CardDescription className="truncate mt-1">
                      {project.source_url}
                    </CardDescription>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-slate-400 hover:text-red-600 -mr-2"
                    onClick={() => setDeleteId(project.id)}
                    data-testid={`delete-project-${project.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4 text-sm text-slate-600 mb-4">
                  <div className="flex items-center gap-1.5">
                    <Package className="w-4 h-4 text-amber-500" />
                    <span>{project.product_count} produktů</span>
                  </div>
                  {project.merge_variants && (
                    <div className="flex items-center gap-1.5">
                      <RefreshCw className="w-4 h-4 text-amber-500" />
                      <span>Merge ON</span>
                    </div>
                  )}
                </div>
                <div className="text-xs text-slate-400 mb-4">
                  Aktualizováno: {formatDate(project.updated_at)}
                </div>
                <div className="flex gap-2">
                  <Button asChild variant="outline" className="flex-1" data-testid={`open-project-${project.id}`}>
                    <Link to={`/projects/${project.id}`}>
                      Otevřít
                    </Link>
                  </Button>
                  <Button asChild variant="ghost" size="icon" data-testid={`external-link-${project.id}`}>
                    <a href={project.source_url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              Smazat projekt
            </DialogTitle>
            <DialogDescription>
              Opravdu chcete smazat tento projekt? Tato akce je nevratná a smaže všechny produkty a verze feedu.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>
              Zrušit
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete}
              disabled={deleting}
              data-testid="confirm-delete-btn"
            >
              {deleting ? 'Mažu...' : 'Smazat'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
